import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Select,
  Switch,
  TabsProps,
} from 'antd';
const FormItem = Form.Item;
const { Option } = Select;
const { TextArea } = Input;
import TicketConfigurationContext from '@totem/components/ticketing/configuration/ticketingConfigurationContext';
import SalesOrderKeysTab from '@totem/components/ticketing/details/dialogs/salesOrderKeys/SalesOrderKeysTab';
import {
  Ticket,
  TicketCategory,
  TicketQueryResult,
} from '@totem/components/ticketing/ticketing';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import OrgHierarchySelector from '@totem/components/ui_components/widgets/hierarchySelector/orgHierarchySelector';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ForeignKey, ObjectReference } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import { isInternalEmailAddress } from '@totem/utilities/security';

const styles = {
  form: {
    width: '100%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
  summaryInput: {
    width: '425px',
  },
};

type Props = {
  open: boolean;
  ticket: TicketQueryResult;
  onClose: (boolean) => void;
};

const HeaderEditDialog = ({ open, ticket, onClose }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [ticketData, setTicketData] = useState<Ticket>(ticket.ticket);
  const [ticketType, setTicketType] = useState<string>(ticket.ticket.type);
  const { filteredCategories } = useContext(TicketConfigurationContext);
  const [categoryOptions, setCategoryOptions] = useState<TicketCategory[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<
    TicketCategory[]
  >([]);
  const [isSending, setIsSending] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [keys, setKeys] = useState<ForeignKey[]>(ticket.ticket.keys ?? []);
  const [keysChanged, setKeysChanged] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();

  const sendTicket = useCallback(
    async (request: Ticket) => {
      if (isSending) {
        return;
      }
      fetch(`${TICKET_ENDPOINT}/${request.id}`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(request),
      })
        .then((response) => {
          if (response.status === 200) {
            onClose(true);
            return response.json();
          }
          message.error('Ticket update failed!');
          return response.json();
        })
        .finally(() => {
          onClose(true);

          setIsSending(false);
        });

      setIsSending(true);
    },
    [isSending],
  );

  useEffect(() => {
    setTicketData(ticket.ticket);

    setTicketType(ticket.ticket.type);

    setKeys(ticket.ticket.keys ?? []);
  }, [ticket]);

  // Update Categories when Ticket Type changes
  useEffect(() => {
    if (
      isNotNull(ticketType) &&
      ticketType !== '' &&
      filteredCategories !== null
    ) {
      const selectedTicketType = filteredCategories.find(
        (chk) => chk.name === ticketType,
      );
      if (
        isNotNull(selectedTicketType) &&
        isNotNull(selectedTicketType.children)
      ) {
        const baseOptions = selectedTicketType.children;
        const originalCategory = ticket.ticket.category;

        // Inject the original ticket's category if it's not in the selectable
        // options (handles system-set categories) — only while on the original type
        const categoryMissing =
          ticketType === ticket.ticket.type &&
          isNotNull(originalCategory) &&
          originalCategory !== '' &&
          !baseOptions.some((opt) => opt.name === originalCategory);

        const options = categoryMissing
          ? [
              { name: originalCategory, children: [] as TicketCategory[] },
              ...baseOptions,
            ]
          : baseOptions;

        setCategoryOptions(options);

        if (options.length > 0 && isNotNull(ticketData)) {
          const categoryValid = options.some(
            (opt) => opt.name === ticketData.category,
          );
          if (!categoryValid) {
            setTicketData((prevState) => ({
              ...prevState,
              category: baseOptions[0].name,
            }));
          }
        }
        return;
      }
    }
    setCategoryOptions([]);
  }, [ticketData.type, filteredCategories]);

  // Update SubCategories when Category changes
  useEffect(() => {
    if (isNotNull(ticketData.category) && ticketData.category !== '') {
      const currentCategory = categoryOptions.find(
        (chk) => chk.name === ticketData.category,
      );
      if (isNotNull(currentCategory)) {
        const baseSubOptions = currentCategory.children ?? [];
        const originalSubCategory = ticket.ticket.subCategory;

        // Inject the original ticket's subCategory if it's not in the selectable
        // options (handles system-set subCategories) — only while on the original category
        const subCategoryMissing =
          ticketData.category === ticket.ticket.category &&
          isNotNull(originalSubCategory) &&
          originalSubCategory !== '' &&
          !baseSubOptions.some((opt) => opt.name === originalSubCategory);

        const subOptions = subCategoryMissing
          ? [
              { name: originalSubCategory, children: [] as TicketCategory[] },
              ...baseSubOptions,
            ]
          : baseSubOptions;

        setSubCategoryOptions(subOptions);

        if (subOptions.length > 0 && isNotNull(ticketData)) {
          const subCategoryValid = subOptions.some(
            (opt) => opt.name === ticketData.subCategory,
          );
          if (!subCategoryValid) {
            setTicketData((prevState) => ({
              ...prevState,
              subCategory: baseSubOptions[0]?.name ?? '',
            }));
          }
        }
        return;
      }
    }
    setSubCategoryOptions([]);
  }, [ticketData.category, filteredCategories, categoryOptions]);

  const isValid = () => {
    return true;
  };

  const handleSubmit = () => {
    setFormSubmitted(true);
    if (isValid()) {
      if (isNotNull(ticketData)) {
        const payload = { ...ticketData };
        sendTicket(payload);
      }
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    if (name === 'type') {
      setTicketType(value);
      if (
        (value === 'Project' || value === 'HR') &&
        ticketData.internalOnly === false
      ) {
        setTicketData((prevState) => ({ ...prevState, internalOnly: true }));
      } else if (ticketData.internalOnly === true) {
        setTicketData((prevState) => ({ ...prevState, internalOnly: false }));
      }
    }
    if (name === 'category') {
      setTicketData((prevState) => ({
        ...prevState,
        [name]: value,
        subCategory: '',
      }));
    } else {
      setTicketData((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  const handleChange = ({
    target: { value, name },
  }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTicketData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleReferenceChange = (
    _org: ObjectReference,
    region: ObjectReference,
    building: ObjectReference,
    controlSystem: ObjectReference,
  ) => {
    setTicketData((prevState) => ({
      ...prevState,
      organizationId: _org.id,
      regionId: region.id,
      buildingId: building.id,
      controlSystemId: controlSystem.id,
    }));
  };

  const getTabItems = (): TabsProps['items'] => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'Description',
        label: 'Description',
        children: (
          <>
            <FormItem label="Description" colon={false} style={styles.formItem}>
              <TextArea
                name="description"
                value={ticketData.description}
                onChange={handleChange}
              />
            </FormItem>
          </>
        ),
      },
      {
        key: 'Categorization',
        label: 'Categorization',
        children: (
          <>
            <FormItem
              label="Category"
              colon={false}
              style={styles.formItem}
              required
              validateStatus={
                formSubmitted === false || ticketData.category !== ''
                  ? 'success'
                  : 'error'
              }
            >
              <Select
                value={ticketData.category}
                onChange={(newValue: string) => {
                  handleSelectChange('category', newValue);
                }}
              >
                <Option value="">--Select--</Option>
                {isNotNull(categoryOptions) &&
                  categoryOptions.map((category) => (
                    <Option key={category.name} value={category.name}>
                      {category.name}
                    </Option>
                  ))}
              </Select>
            </FormItem>
            <FormItem
              label="SubCategory"
              colon={false}
              style={styles.formItem}
              required
              validateStatus={
                formSubmitted === false || ticketData.subCategory !== ''
                  ? 'success'
                  : 'error'
              }
            >
              <Select
                value={ticketData.subCategory}
                onChange={(newValue: string) =>
                  handleSelectChange('subCategory', newValue)
                }
              >
                <Option value="">--Select--</Option>
                {isNotNull(subCategoryOptions) &&
                  subCategoryOptions.map((subCategory) => (
                    <Option key={subCategory.name} value={subCategory.name}>
                      {subCategory.name}
                    </Option>
                  ))}
              </Select>
            </FormItem>
          </>
        ),
      },
      {
        key: 'Location',
        label: 'Location',
        children: (
          <OrgHierarchySelector
            showLabels
            labelPosition={'left'}
            showOrganization={
              permissions.internal.hasPermissions(userProfile) &&
              permissions.ticketingUpdater.hasPermissions(userProfile)
            }
            showRegions={false}
            showBuildings
            showControlSystems
            defaultRegionId={ticket.ticket.regionId}
            defaultBuildingId={ticket.ticket.buildingId}
            defaultControlSystemId={ticket.ticket.controlSystemId}
            onChange={handleReferenceChange}
          />
        ),
      },
    ];

    if (permissions.contractsEditor.hasPermissions(userProfile)) {
      tabItems.push({
        key: 'SalesOrder',
        label: 'Sales Order',
        children: (
          <SalesOrderKeysTab
            ticket={ticket.ticket}
            keys={keys}
            onKeysChanged={(updatedKeys) => {
              setKeys(updatedKeys);

              setKeysChanged(true);
            }}
          />
        ),
      });
    }

    return tabItems;
  };

  return (
    <Modal
      open={open}
      width={800}
      title={`Edit: ${ticket.ticket.number}`}
      onCancel={() => {
        onClose(keysChanged);
      }}
      onOk={handleSubmit}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            onClose(keysChanged);
          }}
        >
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
    >
      {contextHolder}
      <Form>
        <FormItem
          label="Summary"
          colon={false}
          style={styles.formItem}
          required
          validateStatus={
            formSubmitted === false || ticketData.summary.trim() !== ''
              ? 'success'
              : 'error'
          }
        >
          <Input
            name="summary"
            type="text"
            style={styles.summaryInput}
            value={ticketData.summary}
            onChange={handleChange}
            maxLength={50}
          />
        </FormItem>
        <FormItem
          label="Impact"
          colon={false}
          style={styles.formItem}
          required
          tooltip={{
            title: (
              <>
                How broadly this issue affects the business.
                <br />
                <b>High:</b> Organization-wide or critical function down.
                <br />
                <b>Medium:</b> Department or significant group affected.
                <br />
                <b>Low:</b> Single user or non-critical function.
              </>
            ),
          }}
        >
          <Select
            value={ticketData.impact}
            onChange={(newValue: string) =>
              handleSelectChange('impact', newValue)
            }
          >
            <Option value="High">High</Option>
            <Option value="Medium">Medium</Option>
            <Option value="Low">Low</Option>
          </Select>
        </FormItem>
        <FormItem
          label="Urgency"
          colon={false}
          style={styles.formItem}
          required
          tooltip={{
            title: (
              <>
                How quickly this issue needs to be resolved.
                <br />
                <b>High:</b> Immediate action required, no workaround exists.
                <br />
                <b>Medium:</b> Timely but not immediate, partial workaround
                available.
                <br />
                <b>Low:</b> No time pressure, workaround exists.
              </>
            ),
          }}
        >
          <Select
            value={ticketData.urgency}
            onChange={(newValue: string) =>
              handleSelectChange('urgency', newValue)
            }
          >
            <Option value="High">High</Option>
            <Option value="Medium">Medium</Option>
            <Option value="Low">Low</Option>
          </Select>
        </FormItem>
        {permissions.internal.hasPermissions(userProfile) && (
          <FormItem label="Internal Only" colon={false} style={styles.formItem}>
            <Switch
              checked={ticketData.internalOnly}
              onChange={(isChecked: boolean) => {
                const externalPartiesInvolved =
                  ticket.ticket.partiesInvolved.filter(
                    (piv) =>
                      piv.status.toUpperCase() === 'ACTIVE' &&
                      !isInternalEmailAddress(piv.email),
                  );

                if (
                  isChecked &&
                  isNotNull(externalPartiesInvolved) &&
                  externalPartiesInvolved.length > 0
                ) {
                  messageApi.open({
                    type: 'error',
                    content:
                      'You must remove external users from parties involved before changing to INTERNAL ONLY.',
                  });
                  return;
                }

                setTicketData((prevState) => ({
                  ...prevState,
                  internalOnly: isChecked,
                }));
              }}
            />
          </FormItem>
        )}
        <IBTabs defaultActiveKey="Description" items={getTabItems()} />
      </Form>
    </Modal>
  );
};

export default HeaderEditDialog;
